import { useAuth0 } from '@auth0/auth0-react';
import { Feature, FeatureBase } from '@/types';
import { AnyMongoAbility } from '@casl/ability';
import { GetStructureFeaturesQuery } from '@/__generated__/graphql';
import { useCallback } from 'react';

import useAbility from '@/hooks/useAbility';

const hasFeaturePermissions = ({
  feature,
  ability,
}: {
  feature: Feature;
  ability: AnyMongoAbility;
}) => {
  return (
    !feature?.permissions ||
    feature?.permissions?.length === 0 ||
    feature?.permissions?.every((permission) => {
      const items = permission.split(':');
      return permission.includes(':') && ability.can(items[0], items[1]);
    })
  );
};

const hasFeatureRoles = ({ feature, userRoles }: { feature: Feature; userRoles: string[] }) => {
  return (
    !feature?.roles ||
    feature?.roles?.length === 0 ||
    feature?.roles?.some((role) => userRoles.includes(role))
  );
};

export const useFeaturesAccess = () => {
  const { user } = useAuth0();
  const { ability, loading } = useAbility();

  const setFeatureAccessFlags = useCallback(
    ({ data }: { data: GetStructureFeaturesQuery | undefined }) => {
      const userRoles: Array<string> =
        (user?.['http://abyss.com/user/roles'] as Array<string>) ?? [];
      if (!data || !data.structure || !data.structure?.features || !user || loading)
        return undefined;

      const structureFeatures = { ...data?.structure.features };

      Object.keys(data.structure.features).forEach((key) => {
        const mappedFeature = data?.structure?.features[key].map(
          (mainFeature: Feature | string) => {
            if (typeof mainFeature === 'string') {
              return mainFeature;
            }
            let updatedSubNavigation = {};
            if (mainFeature.subNavigation) {
              updatedSubNavigation = mainFeature.subNavigation.map((subFeature: FeatureBase) => {
                return {
                  ...subFeature,
                  enabled:
                    hasFeaturePermissions({ feature: subFeature, ability }) &&
                    hasFeatureRoles({ feature: subFeature, userRoles }),
                };
              });
            }
            return {
              ...mainFeature,
              ...(mainFeature.subNavigation ? { subNavigation: updatedSubNavigation } : {}),
              enabled:
                hasFeaturePermissions({ feature: mainFeature, ability }) &&
                hasFeatureRoles({ feature: mainFeature, userRoles }),
            };
          }
        );
        structureFeatures[key] = mappedFeature;
      });
      return { ...data?.structure, features: structureFeatures };
    },
    [ability, loading, user]
  );

  return { setFeatureAccessFlags };
};
